import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'
import { toast } from 'sonner'
import {
  ArrowLeft,
  MoreHorizontal,
  Plus,
  Pencil,
  Trash2,
  MapPin,
  Calendar,
  Eye,
  Download,
  Target,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import {
  useBuilding,
  useDeleteBuilding,
  useRooms,
  useDeleteRoom,
  useOrganization,
  useUpdateBuilding,
} from '@/hooks'
import {
  PageHeader,
  EmptyState,
  DataTable,
  SortableHeader,
  ConfirmDialog,
  NotFound,
  HistoryTimeline,
  BulkQRPrint,
  BulkQRDownloadDialog,
  RequestPrintDialog,
  ComplianceTargetForm,
  GPSMap,
} from '@/components'
import { slugify } from '@/lib/qr'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { ComplianceTarget, Room } from '@/types'
import { BuildingFormDialog } from './BuildingFormDialog'
import { RoomFormDialog } from './RoomFormDialog'

export function BuildingDetailPage() {
  const { buildingId } = useParams<{ buildingId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { effectiveOrgId } = useOrg()

  const {
    data: building,
    isLoading: buildingLoading,
    error: buildingError,
  } = useBuilding(buildingId ?? '')
  const { data: rooms, isLoading: roomsLoading, error: roomsError } = useRooms(buildingId ?? '')
  const deleteBuildingMutation = useDeleteBuilding(effectiveOrgId)
  const deleteRoomMutation = useDeleteRoom(buildingId ?? '')
  const updateBuildingMutation = useUpdateBuilding(buildingId ?? '', effectiveOrgId)
  // Fetch the org record so the compliance target form can surface the
  // org-default as an "Inheriting from …" hint. A super_admin viewing a
  // building under another org will see that org's default via the normal
  // org_id scoping in api.ts.
  const { data: org } = useOrganization(effectiveOrgId)

  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteBuildingDialogOpen, setDeleteBuildingDialogOpen] = useState(false)
  const [createRoomDialogOpen, setCreateRoomDialogOpen] = useState(false)
  const [qrDownloadOpen, setQRDownloadOpen] = useState(false)
  const [editRoom, setEditRoom] = useState<Room | null>(null)
  const [deleteRoom, setDeleteRoom] = useState<Room | null>(null)

  const canEdit = user?.role === 'super_admin' || user?.role === 'admin'

  const roomColumns: ColumnDef<Room>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
      cell: ({ row }) => (
        <Link to={`/rooms/${row.original.id}`} className="font-medium text-primary hover:underline">
          {row.getValue('name')}
        </Link>
      ),
    },
    {
      accessorKey: 'floor',
      header: ({ column }) => <SortableHeader column={column}>Floor</SortableHeader>,
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.getValue('floor') || '—'}</span>
      ),
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => <SortableHeader column={column}>Created</SortableHeader>,
      cell: ({ row }) => {
        const date = new Date(row.getValue('created_at'))
        return date.toLocaleDateString()
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`/rooms/${row.original.id}`}>
                <Eye className="mr-2 h-4 w-4" />
                View
              </Link>
            </DropdownMenuItem>
            {canEdit && (
              <>
                <DropdownMenuItem onClick={() => setEditRoom(row.original)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDeleteRoom(row.original)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  async function handleSaveTarget(target: ComplianceTarget | null) {
    try {
      await updateBuildingMutation.mutateAsync({ default_target: target })
      toast.success(
        target === null ? 'Building compliance target cleared' : 'Building compliance target saved'
      )
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save target'
      toast.error(msg)
      throw err
    }
  }

  if (buildingLoading) {
    return <div className="text-muted-foreground">Loading building...</div>
  }

  if (buildingError || !building) {
    return <NotFound onBack={() => navigate('/buildings')} />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/buildings">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <PageHeader
          title={building.name}
          description="Building details and rooms"
          actions={
            canEdit && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditDialogOpen(true)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </Button>
                <Button variant="destructive" onClick={() => setDeleteBuildingDialogOpen(true)}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </div>
            )
          }
        />
      </div>

      {/* Building Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Building Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {building.address && (
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <span>{building.address}</span>
            </div>
          )}
          {(user?.role === 'admin' || user?.role === 'super_admin') && building.address && (
            <div className="flex items-center gap-1.5 mt-1">
              {building.latitude != null && building.longitude != null ? (
                <span className="flex items-center gap-1 text-xs text-green-600">
                  <CheckCircle2 className="h-3 w-3" />
                  Location verified
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-amber-600">
                  <AlertCircle className="h-3 w-3" />
                  Location not verified — GPS checks disabled for this building
                </span>
              )}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">
              Created {new Date(building.created_at).toLocaleDateString()}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary">
              {rooms?.length ?? 0} {rooms?.length === 1 ? 'room' : 'rooms'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* GPS Zone Card — only shown when building has been geocoded */}
      {building.latitude != null && building.longitude != null && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="h-5 w-5" />
              GPS Zone
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <GPSMap
              geocodedLat={building.latitude}
              geocodedLng={building.longitude}
              centerLat={building.gps_center_latitude}
              centerLng={building.gps_center_longitude}
              radiusMetres={building.gps_radius_metres ?? 150}
            />
            <p className="text-xs text-muted-foreground">
              <span className="inline-block w-3 h-3 rounded-full bg-gray-500 mr-1 align-middle" />
              Building address ·{' '}
              <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-1 align-middle" />
              Validation center · Radius: {building.gps_radius_metres ?? 150} m
              {canEdit && (
                <>
                  {' · '}
                  <button
                    className="underline"
                    type="button"
                    onClick={() => setEditDialogOpen(true)}
                  >
                    Edit to reposition
                  </button>
                </>
              )}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Compliance Target Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Target className="h-5 w-5" />
            Compliance target
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ComplianceTargetForm
            value={building.default_target ?? null}
            inheritedFrom={
              org?.default_target
                ? {
                    label: 'Organization default',
                    target: org.default_target,
                  }
                : null
            }
            canEdit={canEdit}
            onSave={handleSaveTarget}
            toggleLabel="Override inherited target"
          />
        </CardContent>
      </Card>

      {/* Rooms Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Rooms</h2>
          <div className="flex gap-2">
            {rooms && rooms.length > 0 && (
              <BulkQRPrint rooms={rooms} buildingName={building.name} />
            )}
            {canEdit && rooms && rooms.length > 0 && (
              <Button variant="outline" onClick={() => setQRDownloadOpen(true)}>
                <Download className="mr-2 h-4 w-4" />
                Download QR Codes
              </Button>
            )}
            {canEdit && rooms && rooms.length > 0 && (
              <RequestPrintDialog building={building} rooms={rooms} />
            )}
            {canEdit && (
              <Button onClick={() => setCreateRoomDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Room
              </Button>
            )}
          </div>
        </div>

        {roomsLoading ? (
          <div className="text-muted-foreground">Loading rooms...</div>
        ) : roomsError ? (
          <div className="text-destructive">Failed to load rooms: {roomsError.message}</div>
        ) : rooms && rooms.length > 0 ? (
          <DataTable
            columns={roomColumns}
            data={rooms}
            filterColumn="name"
            filterPlaceholder="Filter rooms..."
          />
        ) : (
          <EmptyState
            title="No rooms"
            description="Add rooms to this building to start tracking cleaning reports."
            action={
              canEdit && (
                <Button onClick={() => setCreateRoomDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Room
                </Button>
              )
            }
          />
        )}
      </div>

      {/* History */}
      <HistoryTimeline
        entityType="buildings"
        entityId={buildingId ?? ''}
        createdAt={building.created_at}
        updatedAt={building.updated_at}
        updatedBy={building.updated_by}
        updatedByName={building.updated_by_name}
      />

      {/* Dialogs */}
      <BuildingFormDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        building={building}
      />

      <ConfirmDialog
        open={deleteBuildingDialogOpen}
        onOpenChange={setDeleteBuildingDialogOpen}
        title="Delete Building"
        description={`Are you sure you want to delete "${building.name}"? It will no longer appear in lists, but existing reports will retain the building name.`}
        confirmText="Delete"
        destructive
        onConfirm={() => {
          deleteBuildingMutation.mutate(building.id, {
            onSuccess: () => navigate('/buildings'),
          })
        }}
        isLoading={deleteBuildingMutation.isPending}
      />

      <RoomFormDialog
        open={createRoomDialogOpen}
        onOpenChange={setCreateRoomDialogOpen}
        buildingId={buildingId ?? ''}
      />

      {editRoom && (
        <RoomFormDialog
          open={!!editRoom}
          onOpenChange={(open) => !open && setEditRoom(null)}
          buildingId={buildingId ?? ''}
          room={editRoom}
        />
      )}

      <BulkQRDownloadDialog
        open={qrDownloadOpen}
        onOpenChange={setQRDownloadOpen}
        rooms={rooms ?? []}
        buildings={[building]}
        zipName={`${slugify(building.name) || 'building'}-qr-codes.zip`}
      />

      <ConfirmDialog
        open={!!deleteRoom}
        onOpenChange={(open) => !open && setDeleteRoom(null)}
        title="Delete Room"
        description={`Are you sure you want to delete "${deleteRoom?.name}"? It will no longer appear in lists, but existing reports will retain the room name.`}
        confirmText="Delete"
        destructive
        onConfirm={() => {
          if (deleteRoom) {
            deleteRoomMutation.mutate(deleteRoom.id, {
              onSuccess: () => setDeleteRoom(null),
            })
          }
        }}
        isLoading={deleteRoomMutation.isPending}
      />
    </div>
  )
}
